import { computed, observable, ObservableMap } from 'mobx';
import * as moment from 'moment';
import { PhotoService, TagType } from '../Services/PhotoService';
import { IStoryFromServer, StoryServices } from '../Services/StoryService';
import { IUserPhoto } from './UserPhotoStore';

export interface IPhotoWithComments {
    index?: number;
    photo: IUserPhoto;
    itemNumber?: number;
}

export interface IStory {
    photosWithComments: Array<IPhotoWithComments>;
    id: number;
    creator_id: number;
    title: string;
    isPrivate: boolean;
    createdAt: string;
    sharedWithFamilyID?: number;
    commentCount: number;
}

export enum StoryViewStyle {
    CAROUSEL = 'CAROUSEL',
    GRID = 'GRID',
    DEFAULT = 'CAROUSEL',
}

export class StoryStore {
    private storyService: StoryServices;
    private photoService: PhotoService;

    @observable
    private _allStoriesByID = new ObservableMap<string, IStory>();

    @observable
    private _storyViewStyle = StoryViewStyle.DEFAULT;

    @computed
    public get storyViewStyle(): StoryViewStyle {
        return this._storyViewStyle;
    }

    @observable
    private _lastViewedStoryId = 0;

    @computed
    public get lastViewedStoryId(): number {
        return this._lastViewedStoryId;
    }

    @observable
    private _storiesAreLoaded = false;

    @computed
    public get storiesAreLoaded(): boolean {
        return this._storiesAreLoaded;
    }

    @observable
    private _storiesPageScrollTo = 0;

    @computed
    public get storiesPageScrollTo(): number {
        return this._storiesPageScrollTo;
    }

    @observable
    private _storiesPageSort = 'Album Name';

    @computed
    public get storiesPageSort(): string {
        return this._storiesPageSort;
    }

    @computed
    public get allStories(): Array<IStory> {
        return Array.from(this._allStoriesByID.values());
    }

    public setStoryViewStyle(style: StoryViewStyle) {
        this._storyViewStyle = style;
    }

    public setLastViewedStoryId(storyId: number) {
        this._lastViewedStoryId = storyId;
    }

    public setStoriesPageScrollTo(scrollto: number) {
        this._storiesPageScrollTo = scrollto;
    }

    public setStoriesPageSort(sort: string) {
        this._storiesPageSort = sort;
    }

    public getStoryByID(id: number): IStory {
        const foundStory = this._allStoriesByID.get(id.toString())!;

        if (foundStory && foundStory.photosWithComments) {
            foundStory.photosWithComments.sort((b, a) => {
                if (typeof(a.index) !== 'undefined' && typeof(b.index) !== 'undefined') {
                    return a.index - b.index;
                }
                return 0;
            });
        }

        return foundStory;
    }

    public async getOrLoadStoryByID(id: number): Promise<IStory> {
        const foundStory = this._allStoriesByID.get(id.toString());
        if (foundStory) {
            return foundStory;
        }
        const [story] = await this.storyService.getStoryByID(id);
        return this.buildStoryFromServerStory(story);
    }

    public getStoriesForPhotoID(id: number): Promise<Array<IStoryFromServer>> {
        return this.storyService.getStoriesForPhotoID(id);
    }

    public removePhotoFromStory(photoID: number, storyID: number): Promise<void> {
        const storyPhotos = this._allStoriesByID.get(storyID + '')!.photosWithComments;
        const removeIndex = storyPhotos.findIndex(photo => photo.photo.id === photoID);
        this._allStoriesByID.get(storyID + '')!.photosWithComments.splice(removeIndex, 1);

        return this.storyService.removePhotoFromCollection(storyID, photoID);
    }

    public async addPhotosToStory(storyID: number, photoIDs: Array<number>): Promise<void> {
        await this.storyService.addPhotosToStory(storyID, photoIDs);

        // const allPhotos = await this.photoService.getAllPhotosForCurrentUser();
        // const addedPhotos = allPhotos.filter(img => photoIDs.some(p => img.id === p));
        // addedPhotos.forEach(photo => {
        //     const story = this._allStoriesByID.get(storyID.toString());
        //     let lastIndex = story!.photosWithComments.length - 1;
        //     let last = story!.photosWithComments[lastIndex];
        //     if (last && last.index) {
        //         photo.index = last.index;
        //     }
        //     story!.photosWithComments.push({
        //         photo
        //     });
        // });
        await this.getAllStories();
    }

    public async reOrderPhotosInStory(storyID: number, items: Array<any>): Promise<void> {
        console.log('reOrderPhotosInStory()');
        let ids = items.map((item: any) => {
            return item.photo.id;
        });
        await this.storyService.reOrderPhotosInStory(storyID, ids);
        await this.getAllStories();
    }

    public deleteStory(id: number): void {
        this._allStoriesByID.delete(id.toString());
        this.storyService.deleteStory(id);
    }
    public constructor(token: string) {
        this.storyService = new StoryServices(token);
        this.photoService = new PhotoService(token);
        this.getAllStories();
    }

    public async getAllStories(): Promise<void> {
        const response = await this.storyService.getAllStories();
        const allStories = await Promise.all(response.map(this.buildStoryFromServerStory));

        const allStoriesByID = allStories.reduce((map, story) => map.set(story.id.toString(), story), new ObservableMap<string, IStory>());

        this._allStoriesByID = allStoriesByID;
        this._storiesAreLoaded = true;
    }

    public updateStoryTitle(storyID: number, title: string): void {
        this.storyService.updateStoryTitle(storyID, title);
        const foundItem = this._allStoriesByID.get(storyID.toString())!;
        foundItem.title = title;
    }

    public updateFamilyShare(familyID: number | undefined, storyID: number): void {
        this.storyService.updateFamilyShare(familyID, storyID);
        const foundItem = this._allStoriesByID.get(storyID.toString())!;
        foundItem.sharedWithFamilyID = familyID;
        foundItem.isPrivate = familyID === undefined;
    }

    public async createStory(title: string, family_id?: number): Promise<IStoryFromServer> {
        const response = await this.storyService.createStory(title, family_id);
        await this.getAllStories();
        return response;
    }

    private buildStoryFromServerStory = async (story: IStoryFromServer): Promise<IStory> => {
        const allPhotos = await this.storyService.getAllPhotosForStory(story.id);
        let itemNumber = 1;
        const photosWithComments = allPhotos.map(p => ({
            photo: {
                ...p,
                description: {
                    location: p.tags ? (p.tags as any)[TagType.LOCATION] : '',
                    filename: p.tags ? (p.tags as any)[TagType.FILENAME] : '',
                    date: p.tags ? (p.tags as any)[TagType.DATE] : '',
                    rotateDegrees: p.rotate_degrees
                }
            },
            itemNumber: itemNumber ++,
            index: ((typeof(p.index) !== 'undefined') ? p.index + 1 : 0),
            comments: []
        }));

        const commentCount = await this.storyService.getCommentCountForStory(story.id);
        // console.log('story ' + story.id + ' isPrivate: ' + (!story.family_id));
        let storyObject = {
            photosWithComments,
            id: story.id,
            creator_id: story.user_id,
            title: story.title,
            isPrivate: !story.family_id,
            createdAt: story.created_at,
            sharedWithFamilyID: story.family_id,
            commentCount
        };
        this._allStoriesByID.set(storyObject.id.toString(), storyObject);
        return storyObject;
    }
}



// WEBPACK FOOTER //
// ./src/Stores/StoryStore.ts